import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Logger from '../../../common/logger';
import {
    AdminProduct,
    useCreateProductMutation,
    useUpdateProductMutation,
} from '../../../api/group/groupAdminApiSlice';
import CurrencyAmountInput from '../../../components/CurrencyAmountInput';
import TaxCategorySelect from '../../../components/tax/TaxCategorySelect';
import { useDefaultTaxCategoryId } from '../../../components/tax/useDefaultTaxCategoryId';
import CustomButton from '../../../components/CustomButton';
import ImageSlot from '../../../components/ImageSlot';
import LabelWithHelp from '../../../components/LabelWithHelp';
import { isValidAmountInput } from '../../../utils/amountValidation';
import { errorMessageFrom } from '../../../utils/errorMessage';
import SelectField from '../../../components/ui/SelectField';
import { useTenantProductCategoriesQuery } from '../../../hooks/useTenantProductCategoriesQuery';
import CustomModal from '../../common/CustomModal';
import { isRoomProduct } from '../serviceConfigurationRules';
import EquipmentChecklistEditor from '../../../components/equipment/EquipmentChecklistEditor';
import {
    ChecklistRow,
    checklistComplete,
    checklistKeys,
    fromChecklistRows,
    hasEquipmentCheck,
    limitText,
    parseLimitInput,
    toChecklistRows,
} from '../../../utils/stationDefaults';

// Client mirror of the backend upload restriction — the allowlist is
// PRODUCT_IMAGE_EXTENSIONS in new-backend/admin/src/lib/product/
// productFields.ts (multer fileFilter + server-side key naming), 5MB cap
// from productRoutes. The two lists must stay identical.
const ALLOWED_IMAGE_MIME = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
]);
const MAX_IMAGE_FILE_BYTES = 5 * 1024 * 1024;

const fieldClass =
    'w-full border border-foreground/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary';

/**
 * The starting text for a new product's description, per use case.
 *
 * Read with a FIXED language, not the admin's: these fill the *German* and
 * *English* description fields, and which one an operator is reading the UI in
 * says nothing about which language a field holds. A use case with no default
 * simply starts empty (`defaultValue: ''`) — the same graceful miss the
 * per-use-case name placeholders already make, rather than a raw i18n key on
 * screen. Defaults exist for the three use cases whose description this form
 * edits (`TARIFF_USE_CASES` — sup, rental, storage). Room products (#2923)
 * have no description field here — each object keeps its own — so a default
 * for them would be text nothing can reach.
 *
 * They are a starting point, not a fact sheet — deliberately free of anything
 * that could be untrue for a given tenant (sizes, contents, access times,
 * opening hours). Both fields are seeded so an English-speaking customer is not
 * shown German by default; the cost is that an operator who rewrites one and
 * not the other ships two descriptions that disagree, which is visible in this
 * dialog because the two fields sit next to each other.
 */
const defaultDescriptionFor = (
    fixedT: (key: string, opts: { defaultValue: string }) => string,
    useCase: string,
): string => (useCase
    ? fixedT(`services.products.form.description_default_${useCase}`, { defaultValue: '' })
    : '');

interface Props {
    /** The product to edit, or null to create a new one. */
    product: AdminProduct | null;
    /** Use cases the create select offers — the tenant's services restricted
     *  by the parent to those listed in the products section. */
    useCaseOptions: string[];
    onClose: () => void;
    /** Called after a successful save, before closing. */
    onSaved?: () => void;
}

/**
 * Room products (venue/apartment, #2923) use the same form with fewer fields
 * — name, deposit, VAT and category; their objects keep their own
 * description and photos — and get their prices in `RoomProductPricesDialog`.
 *
 * Create / edit a rental product (docs/plans/service-configuration-area.md,
 * product-form step; product model Flow 2). The §1a base fields for the
 * station-rental family: bilingual name and description, featured image,
 * deposit, use case. Prices stay where they are edited today — the tariff
 * editor on the price structure below (the created product's empty price list
 * appears there, or born as zero rows when the use case is declared free).
 *
 * Mounted per product (create: key 'new'), so the field state seeds from the
 * row once — the edit-form rule: validation and payload derive from form
 * state, never the API response.
 */
/**
 * The fields both saves send. A room product's description and image are left
 * out entirely — absent, so the backend leaves them alone, rather than cleared:
 * each object keeps its own (#2923). Module-level so the component stays under
 * the complexity gate.
 */
const sharedSaveFields = (
    isRoom: boolean,
    fields: {
        nameDe: string;
        nameEn: string;
        depositAmount: string;
        taxCategoryId: string;
        productCategoryId: string;
        descriptionDe: string;
        descriptionEn: string;
        featuredImage: File | null;
        /** D15 defaults; absent = the form does not offer the field. */
        maxLimit?: string;
        equipment?: string;
    },
) => {
    const { descriptionDe, descriptionEn, featuredImage, ...always } = fields;
    if (isRoom) return always;
    return {
        ...always,
        descriptionDe,
        descriptionEn,
        ...(featuredImage ? { featuredImage } : {}),
    };
};

interface ProductDefaultsFieldsProps {
    offersLimit: boolean;
    offersChecklist: boolean;
    maxLimit: string;
    limitValid: boolean;
    checklist: ChecklistRow[];
    checklistValid: boolean;
    onMaxLimitChange: (value: string) => void;
    onChecklistChange: (rows: ChecklistRow[]) => void;
}

/**
 * D15 (product refactor 4.4): the product's DEFAULT per-booking limit and
 * equipment checklist. A station may override either on its own page; these
 * fields never write a station. Module-level so the form stays under the
 * complexity gate.
 */
const ProductDefaultsFields = ({
    offersLimit, offersChecklist, maxLimit, limitValid, checklist, checklistValid,
    onMaxLimitChange, onChecklistChange,
}: ProductDefaultsFieldsProps) => {
    const { t } = useTranslation();
    return (
        <>
            {offersLimit && (
                <div className={'flex flex-col gap-1'}>
                    <LabelWithHelp
                        className={'mb-0'}
                        text={t('services.products.form.max_limit_label')}
                        help={t('services.products.form.max_limit_help')}
                    />
                    <input
                        id={'product-max-limit'}
                        aria-label={t('services.products.form.max_limit_label')}
                        type={'text'}
                        inputMode={'numeric'}
                        className={fieldClass}
                        value={maxLimit}
                        placeholder={t('services.products.form.max_limit_placeholder')}
                        onChange={(e) => onMaxLimitChange(e.target.value)}
                    />
                    {!limitValid && (
                        <p className={'text-sm text-foreground'}>
                            {t('services.products.form.max_limit_invalid')}
                        </p>
                    )}
                </div>
            )}
            {offersChecklist && (
                <div className={'flex flex-col gap-2'}>
                    <LabelWithHelp
                        className={'mb-0'}
                        text={t('services.products.form.equipment_label')}
                        help={t('services.products.form.equipment_help')}
                    />
                    <EquipmentChecklistEditor
                        idPrefix={'product-equipment'}
                        rows={checklist}
                        onChange={onChecklistChange}
                    />
                    {!checklistValid && (
                        <p className={'text-sm text-foreground'}>
                            {t('equipment_checklist.labels_required')}
                        </p>
                    )}
                </div>
            )}
        </>
    );
};

/**
 * Which D15 fields the form offers and whether they hold a savable value. A
 * room's Group is one room and carries neither value (D15); a checklist only
 * where a booking shows the equipment check.
 */
const defaultsRules = (
    isRoom: boolean, useCase: string, maxLimit: string, checklist: ChecklistRow[], checklistTouched: boolean,
) => {
    const offersLimit = !isRoom;
    const offersChecklist = !isRoom && hasEquipmentCheck(useCase);
    const limitValid = parseLimitInput(maxLimit).ok;
    // Only a checklist this edit changed is checked and sent: a stored item
    // that predates the label rule must not block, say, a rename.
    const checklistValid = !checklistTouched || checklistComplete(checklist);
    return {
        offersLimit,
        offersChecklist,
        limitValid,
        checklistValid,
        defaultsValid: (!offersLimit || limitValid) && (!offersChecklist || checklistValid),
        sendChecklist: offersChecklist && checklistTouched,
    };
};

/**
 * The D15 defaults as the save sends them; absent where the form does not
 * offer the field, and the checklist only when this edit changed it.
 */
const defaultsSaveFields = (
    offersLimit: boolean,
    sendChecklist: boolean,
    maxLimit: string,
    checklist: ChecklistRow[],
    reservedKeys: string[],
) => ({
    ...(offersLimit ? { maxLimit: maxLimit.trim() } : {}),
    ...(sendChecklist
        ? { equipment: checklist.length > 0 ? JSON.stringify(fromChecklistRows(checklist, reservedKeys)) : '' }
        : {}),
});

const createDescriptionKeyFor = (isRoom: boolean): string => (isRoom
    ? 'services.products.form.room_create_description'
    : 'services.products.form.create_description');

const ProductFormDialog = ({ product, useCaseOptions, onClose, onSaved }: Props) => {
    const { t, i18n } = useTranslation();
    const isEdit = !!product;

    const germanT = useMemo(() => i18n.getFixedT('de'), [i18n]);
    const englishT = useMemo(() => i18n.getFixedT('en'), [i18n]);
    const firstUseCase = useCaseOptions[0] ?? '';

    const [nameDe, setNameDe] = useState(
        product?.langNames?.de?.title ?? product?.name ?? '',
    );
    const [nameEn, setNameEn] = useState(product?.langNames?.en?.title ?? '');
    // Seeded from the use case the select opens on, so a new product carries a
    // description from the first render rather than only after the operator
    // touches the select.
    const [descriptionDe, setDescriptionDe] = useState(() => (isEdit
        ? (product?.langNames?.de?.description ?? product?.description ?? '')
        : defaultDescriptionFor(germanT, firstUseCase)));
    const [descriptionEn, setDescriptionEn] = useState(() => (isEdit
        ? (product?.langNames?.en?.description ?? '')
        : defaultDescriptionFor(englishT, firstUseCase)));
    /**
     * Whether the operator has written in either description field.
     *
     * Switching the use case re-seeds the defaults, but only while they are
     * still ours — re-seeding over typed text would silently discard it. Set
     * from the field's own onChange rather than an effect: one writer per piece
     * of state, no second effect racing the setters (CLAUDE.md, "useEffect:
     * Separate Init and Reaction"). Editing an existing product never re-seeds
     * at all, because its use case is locked.
     */
    const [descriptionEdited, setDescriptionEdited] = useState(false);
    const [deposit, setDeposit] = useState(
        product?.depositAmount != null ? String(product.depositAmount) : '',
    );
    const [hasBadDeposit, setHasBadDeposit] = useState(false);
    // #1353. '' = unassigned, which the price preview reports as such rather
    // than resolving a default rate (ADR-0018 applied to tax).
    //
    // #2959: a NEW product starts on the tenant's default category, shown in
    // the picker so the operator sees it and can change it. `null` = not yet
    // touched, so the default still applies when the catalogue answers after
    // the dialog opened — derived, not copied in by an effect. An edited
    // product keeps exactly what it has, unassigned included.
    const defaultTaxCategoryId = useDefaultTaxCategoryId();
    const [taxCategoryChoice, setTaxCategoryId] = useState<string | null>(
        isEdit ? (product?.taxCategoryId ?? '') : null,
    );
    const taxCategoryId = taxCategoryChoice ?? defaultTaxCategoryId;
    // ADR-0026. '' = "no choice made" — the server files the product under its
    // use case's STANDARD category (§AH), which the picker shows as selected.
    const [productCategoryId, setProductCategoryId] = useState(
        product?.productCategoryId ?? '',
    );
    const [useCase, setUseCase] = useState(firstUseCase);
    // D15 (product refactor 4.4): the product's default per-booking limit and
    // equipment checklist; a station may override either on its own page.
    const [maxLimit, setMaxLimit] = useState(limitText(product?.maxLimit));
    const [checklist, setChecklist] = useState<ChecklistRow[]>(
        () => toChecklistRows(product?.equipment),
    );
    // Set by the editor's own onChange (one writer): only a changed list is sent.
    const [checklistTouched, setChecklistTouched] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const {
        data: allCategories,
        isLoading: categoriesLoading,
        isError: categoriesError,
    } = useTenantProductCategoriesQuery();

    const [createProduct, { isLoading: creating }] = useCreateProductMutation();
    const [updateProduct, { isLoading: updating }] = useUpdateProductMutation();
    const isLoading = creating || updating;

    const filePreview = useMemo(
        () => (imageFile ? URL.createObjectURL(imageFile) : null),
        [imageFile],
    );
    useEffect(() => () => {
        if (filePreview) URL.revokeObjectURL(filePreview);
    }, [filePreview]);
    const previewSrc =
        filePreview ?? (imageRemoved ? null : product?.featuredImage ?? null);

    const trimmedDeposit = deposit.trim();
    const depositValid = !hasBadDeposit && isValidAmountInput(trimmedDeposit);
    // The use case is required: it decides where the product is placed and
    // how it is priced, and it cannot be set later (PO 2026-11-03).
    const useCaseMissing = !isEdit && !useCase;

    const handleSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!file) return;
        if (!ALLOWED_IMAGE_MIME.has(file.type.toLowerCase())) {
            setFormError(t('services.products.form.image_invalid_type'));
            return;
        }
        if (file.size > MAX_IMAGE_FILE_BYTES) {
            setFormError(t('services.products.form.image_too_large'));
            return;
        }
        setFormError(null);
        setImageFile(file);
        setImageRemoved(false);
    };

    // The use case the form is about — the create select's value, or the edited
    // product's own (locked after creation). Drives the per-use-case name
    // example; an unforeseen use case simply gets no placeholder rather than a
    // raw i18n key.
    const effectiveUseCase = isEdit
        ? (product?.useCase ?? product?.compartmentTypes[0]?.toLowerCase() ?? '')
        : useCase;
    /**
     * A ROOM product (#2923) is a type its objects are assigned to: each
     * object keeps its own name, description and photos, and the backend no
     * longer copies a room product's onto them. So the form offers neither —
     * a field that saves and changes nothing the customer sees is the
     * failure-as-absence shape.
     */
    const isRoom = isRoomProduct({ useCase: effectiveUseCase });
    const { offersLimit, offersChecklist, limitValid, checklistValid, defaultsValid, sendChecklist } = defaultsRules(
        isRoom, effectiveUseCase, maxLimit, checklist, checklistTouched,
    );
    const isValid = nameDe.trim().length > 0 && depositValid && !useCaseMissing && defaultsValid;

    const sharedFields = sharedSaveFields(isRoom, {
        nameDe: nameDe.trim(),
        nameEn,
        depositAmount: trimmedDeposit,
        taxCategoryId,
        productCategoryId,
        descriptionDe,
        descriptionEn,
        featuredImage: imageFile,
        ...defaultsSaveFields(
            offersLimit, sendChecklist, maxLimit, checklist, checklistKeys(product?.equipment),
        ),
    });
    const removeImage = imageRemoved && !imageFile && !isRoom;

    const handleSave = async () => {
        if (!isValid || isLoading) return;
        setFormError(null);
        try {
            if (isEdit && product) {
                await updateProduct({
                    productId: product.id,
                    ...sharedFields,
                    ...(removeImage ? { removeFeaturedImage: true } : {}),
                }).unwrap();
            } else {
                await createProduct({ ...sharedFields, useCase }).unwrap();
            }
            onSaved?.();
            onClose();
        } catch (err) {
            Logger('ProductFormDialog').error(`Product save failed: ${JSON.stringify(err)}`);
            // The server's own message where it has one. A category rejected
            // for the wrong use case, or a deposit the shared rule refused,
            // each say what to change; the generic line says only that it did
            // not work, which is the same sentence for every cause.
            //
            // A 404 on an edit is a tab left open after the product was
            // deleted (#2636): say so, in the user's language, rather than
            // the server's bare "product not found".
            const status = (err as { status?: unknown } | null)?.status;
            setFormError(product && status === 404
                ? t('services.products.delete.error_stale')
                : errorMessageFrom(err, t('services.products.form.save_error'), t));
        }
    };

    const textField = (
        id: string,
        label: string,
        value: string,
        onChange: (v: string) => void,
        multiline = false,
        placeholder?: string,
    ) => (
        <div className={'flex flex-col gap-1'}>
            <label className={'text-sm font-medium text-foreground'} htmlFor={id}>
                {label}
            </label>
            {multiline ? (
                <textarea
                    id={id}
                    className={fieldClass}
                    rows={3}
                    value={value}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                />
            ) : (
                <input
                    id={id}
                    type={'text'}
                    className={fieldClass}
                    value={value}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                />
            )}
        </div>
    );

    const namePlaceholder = t(
        `services.products.form.name_placeholder_${effectiveUseCase}`,
        { defaultValue: '' },
    );

    /**
     * Radix forbids an empty `value` on an option, so "no category" needs a
     * sentinel. Without one the picker is a ONE-WAY DOOR: `''` is a supported
     * and meaningful state server-side — the product is simply not filed — but
     * once an id is chosen there is no option to go back to.
     */
    const NO_CATEGORY = '__none__';

    /**
     * The categories this product may be filed under: its own use case only.
     *
     * The backend refuses any other (ADR-0026 §H), so offering one would be a
     * choice the operator cannot save. On create the list follows the use-case
     * select, which is why it is derived rather than fetched per selection.
     */
    const categoryOptions = useMemo(
        () => (allCategories ?? [])
            .filter((c) => c.useCase === effectiveUseCase)
            // The standard category first (ADR-0026 §AH), then by name.
            .sort((a, b) =>
                Number(!!b.isStandard) - Number(!!a.isStandard) || a.name.localeCompare(b.name)),
        [allCategories, effectiveUseCase],
    );
    // Every product belongs to exactly one category, and the standard one is
    // preselected (§AH). Only where it is missing — a legacy tenant, or a list
    // from before it existed — does "no category" remain its own option.
    const standardCategory = categoryOptions.find((c) => c.isStandard);

    return (
        <CustomModal
            open={true}
            title={isEdit
                ? t('services.products.form.edit_title')
                : t('services.products.form.create_title')}
            description={isEdit ? (product?.name ?? undefined) : t(createDescriptionKeyFor(isRoom))}
            onClose={onClose}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />
            }
            saveButton={
                <CustomButton
                    title={t('common.save')}
                    loading={isLoading}
                    disabled={!isValid || isLoading}
                    onClick={handleSave}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                {isEdit && isRoom && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('services.products.form.room_create_description')}
                    </p>
                )}
                {!isEdit && (
                    <div className={'flex flex-col gap-1'}>
                        {/* Concept + consequence beyond the label (the house
                            tooltip rule): the use case decides placement and
                            pricing logic, and is locked after creation. */}
                        <LabelWithHelp
                            className={'mb-0'}
                            text={t('services.products.form.use_case_label')}
                            help={t('services.products.form.use_case_help')}
                        />
                        <select
                            id={'product-use-case'}
                            className={fieldClass}
                            aria-required={true}
                            aria-invalid={useCaseMissing}
                            value={useCase}
                            onChange={(e) => {
                                const next = e.target.value;
                                setUseCase(next);
                                // The category belongs to a use case, so a
                                // change here invalidates it. Left standing it
                                // would submit a category of the PREVIOUS use
                                // case — rejected by the backend, and until
                                // then shown as a selection the picker no
                                // longer even lists.
                                setProductCategoryId('');
                                if (!descriptionEdited) {
                                    setDescriptionDe(defaultDescriptionFor(germanT, next));
                                    setDescriptionEn(defaultDescriptionFor(englishT, next));
                                }
                            }}
                        >
                            {useCaseOptions.map((option) => (
                                <option key={option} value={option}>
                                    {t(`service_type_selector.${option}`, { defaultValue: option })}
                                </option>
                            ))}
                        </select>
                        {useCaseMissing && (
                            <p className={'text-sm text-foreground'}>
                                {t('services.products.form.use_case_required')}
                            </p>
                        )}
                    </div>
                )}

                {textField(
                    'product-name-de',
                    t('services.products.form.name_de_label'),
                    nameDe,
                    setNameDe,
                    false,
                    namePlaceholder || undefined,
                )}
                {/* The English pair marks its optionality in the placeholder
                    (station-wizard convention) and states the real fallback:
                    the end-user app shows the German text when no English one
                    exists (getEquipmentName, apps/end-user). */}
                {textField(
                    'product-name-en',
                    t('services.products.form.name_en_label'),
                    nameEn,
                    setNameEn,
                    false,
                    t('services.products.form.name_en_placeholder'),
                )}
                {!isRoom && (
                    <>
                        {textField(
                            'product-description-de',
                            t('services.products.form.description_de_label'),
                            descriptionDe,
                            (value) => {
                                setDescriptionEdited(true);
                                setDescriptionDe(value);
                            },
                            true,
                            t('services.products.form.description_de_placeholder'),
                        )}
                        {textField(
                            'product-description-en',
                            t('services.products.form.description_en_label'),
                            descriptionEn,
                            (value) => {
                                setDescriptionEdited(true);
                                setDescriptionEn(value);
                            },
                            true,
                            t('services.products.form.description_en_placeholder'),
                        )}

                        {/* Preview the crop the customer actually gets. Every end-user
                        surface renders this image as a SQUARE with `object-cover` —
                        64px in the storage, rental, p2p and keys accordions, 80px on
                        the apartment card — so the 16:9 default promised a framing
                        nobody ever sees and quietly cut the sides off. Capped in
                        width because a full-width square would dominate the dialog. */}
                        <div className={'max-w-[220px]'}>
                            <ImageSlot
                                label={t('services.products.form.image_label')}
                                help={t('services.products.form.image_help')}
                                previewSrc={previewSrc}
                                onSelect={handleSelectFile}
                                onRemove={() => {
                                    setImageFile(null);
                                    setImageRemoved(true);
                                }}
                                chooseLabel={t('services.products.form.choose_image')}
                                removeLabel={t('services.products.form.remove_image')}
                                noImageLabel={t('services.products.form.no_image')}
                                aspectClass={'aspect-square'}
                            />
                        </div>

                    </>
                )}

                <CurrencyAmountInput
                    id={'product-deposit'}
                    label={t('services.products.form.deposit_label')}
                    value={deposit}
                    onValueChange={(value, badInput) => {
                        setDeposit(value);
                        setHasBadDeposit(badInput);
                        setFormError(null);
                    }}
                    hint={t('services.products.form.deposit_hint')}
                    validationMessage={
                        !depositValid && (trimmedDeposit.length > 0 || hasBadDeposit)
                            ? t('services.deposits.edit_deposit.validation')
                            : null
                    }
                />

                <ProductDefaultsFields
                    offersLimit={offersLimit}
                    offersChecklist={offersChecklist}
                    maxLimit={maxLimit}
                    limitValid={limitValid}
                    checklist={checklist}
                    checklistValid={checklistValid}
                    onMaxLimitChange={(value) => {
                        setMaxLimit(value);
                        setFormError(null);
                    }}
                    onChecklistChange={(rows) => {
                        setChecklist(rows);
                        setChecklistTouched(true);
                        setFormError(null);
                    }}
                />

                <TaxCategorySelect
                    value={taxCategoryId}
                    onChange={(value) => {
                        setTaxCategoryId(value);
                        setFormError(null);
                    }}
                    help={t('tax_category.help')}
                />

                {/* Only categories of THIS product's use case are offered. The
                    backend refuses any other (ADR-0026 §H), so offering one
                    would be a choice that cannot be saved. */}
                <SelectField
                    label={t('services.products.form.category_label')}
                    labelAbove={true}
                    help={t('services.products.form.category_help')}
                    value={productCategoryId || standardCategory?.id || NO_CATEGORY}
                    onChange={(value) => {
                        setProductCategoryId(value === NO_CATEGORY ? '' : value);
                        setFormError(null);
                    }}
                    // The standard product category is a real row and always
                    // offered (ADR-0026 §AE). Only a FAILED load stays empty,
                    // so `noOptions` can say so rather than offering one
                    // harmless-looking choice: a failure wearing the shape of
                    // an answer.
                    options={categoriesError
                        ? []
                        : [
                            ...(standardCategory ? [] : [NO_CATEGORY]),
                            ...categoryOptions.map((c) => c.id),
                        ]}
                    optionLabels={{
                        [NO_CATEGORY]: t('services.products.form.category_none'),
                        ...Object.fromEntries(categoryOptions.map((c) => [c.id, c.name])),
                    }}
                    loading={categoriesLoading}
                    placeholder={t('services.products.form.category_none')}
                    noOptions={t('services.products.form.category_load_error')}
                />

                {/* A room product's prices are set in the room price dialog,
                    which opens once it is created; editing it, they are one
                    click away on its row. */}
                {!isRoom && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('services.products.form.prices_hint')}
                    </p>
                )}
                {isRoom && !isEdit && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('services.products.form.room_prices_hint')}
                    </p>
                )}

                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default ProductFormDialog;
